import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import ReviewQueueFilters from './augmentation-review/ReviewQueueFilters';
import ReviewQueueTable from './augmentation-review/ReviewQueueTable';
import JobDetailModal from './augmentation-review/JobDetailModal';
import { useAugmentationReview } from '../hooks/useAugmentationReview';

const AugmentationReviewQueuePage = ({ customerId }) => {
  const {
    reviewQueue,
    customers,
    loading,
    error,
    success,
    selectedCustomerId,
    setSelectedCustomerId,
    fetchReviewQueue,
    fetchJobDetail,
    resolveJob,
  } = useAugmentationReview();

  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Set the customer ID from prop when provided
  useEffect(() => {
    if (customerId && customerId !== selectedCustomerId) {
      setSelectedCustomerId(customerId);
    }
  }, [customerId, selectedCustomerId, setSelectedCustomerId]);

  const handleViewDetail = async (job) => {
    try {
      setLoadingDetail(true);
      const jobDetail = await fetchJobDetail(job.id);
      setSelectedJob(jobDetail);
      setShowDetailModal(true);
    } catch (err) {
      // Error already handled in hook
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCloseDetail = () => {
    setShowDetailModal(false);
    setSelectedJob(null);
  };

  const handleResolveJob = async (resolution) => {
    if (!selectedJob) return;
    
    try {
      await resolveJob(selectedJob.id, resolution);
      handleCloseDetail();
    } catch (err) {
      // Error already handled in hook
      throw err;
    }
  };

  const handleRefresh = () => {
    fetchReviewQueue();
  };

  return (
    <div className="user-list">
      <div className="user-list-header">
        <h2>Augmentation Review Queue</h2>
        <div>
          <button
            onClick={handleRefresh}
            className="btn btn-outline-primary"
            disabled={loading}
            title="Refresh queue"
          >
            <FontAwesomeIcon icon={faSync} spin={loading} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      )}

      <ReviewQueueFilters
        customers={customerId ? [] : customers}
        selectedCustomerId={selectedCustomerId}
        onCustomerChange={customerId ? null : setSelectedCustomerId}
        loading={loading}
        hideCustomerFilter={!!customerId}
      />

      {loading && !customerId && customers.length === 0 ? (
        <div>Loading customers...</div>
      ) : loading ? (
        <div>Loading review queue...</div>
      ) : reviewQueue.length === 0 ? (
        <div className="alert alert-info">
          {selectedCustomerId
            ? 'No jobs in review queue for the selected customer.'
            : 'No jobs in review queue. All augmentation jobs have been processed!'}
        </div>
      ) : (
        <>
          <div className="mb-3">
            <span className="text-muted">
              {reviewQueue.length} job{reviewQueue.length !== 1 ? 's' : ''} requiring review
            </span>
          </div>
          <ReviewQueueTable jobs={reviewQueue} onViewDetail={handleViewDetail} />
        </>
      )}

      {/* Job Detail Modal */}
      {showDetailModal && selectedJob && (
        <JobDetailModal
          show={showDetailModal}
          job={selectedJob}
          onClose={handleCloseDetail}
          onResolve={handleResolveJob}
          loading={loadingDetail}
        />
      )}
    </div>
  );
};

AugmentationReviewQueuePage.propTypes = {
  customerId: PropTypes.string,
};

AugmentationReviewQueuePage.defaultProps = {
  customerId: null,
};

export default AugmentationReviewQueuePage;
